import { t } from "i18next";
import { timeRange } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import { isNilOrEmpty } from "ramda-adjunct";
import { array, InferType, object, string } from "yup";

const timeToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(":").map(Number);

  return hours * 60 + minutes;
};

/**
 * Value of the "Attendance beginning on national holiday" condition: which country's holidays count,
 * which of them are opted out, and the time windows within such a day (an empty list means the whole
 * day, the same convention the weekday rows of the plain beginning condition use).
 */
export const nationalHolidayConditionSchema = object({
  // nullable so a freshly added condition row has something to render, but a value without a country
  // never matches anything, so it is rejected before it can be saved
  country: string()
    .defined()
    .default(null)
    .nullable()
    .test("country-required", () => t("required"), isNotBlank),
  excludedHolidays: array().of(string().required()).default([]),
  times: array()
    .of(timeRange)
    .test("no-overlapping-intervals", () => t("timeOverlapping"), hasNoOverlap)
    .default([]),
});

export type NationalHolidayConditionFormFields = InferType<typeof nationalHolidayConditionSchema>;

function isNotBlank(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && value !== "";
}

function hasNoOverlap(times: InferType<typeof timeRange>[] | undefined): boolean {
  if (isNilOrEmpty(times) || (times ?? []).length <= 1) {
    return true; // no overlap possible with 0 or 1 intervals
  }

  const ranges = (times ?? [])
    .filter(({ from, to }) => Boolean(from) && Boolean(to))
    .map(({ from, to }) => ({ from: timeToMinutes(from!), to: timeToMinutes(to!) }));

  return !ranges.some((range, index) =>
    ranges.some((other, otherIndex) => index !== otherIndex && other.to >= range.from && other.from <= range.to)
  );
}
